
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import PerformanceOptimizer from "@/components/PerformanceOptimizer";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Book from "./pages/Book";
import Admin from "./pages/Admin";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Resources from "./pages/Resources";
import { Chicago } from "./pages/locations";
import StJoseph from "./pages/locations/StJoseph";
import Niles from "./pages/locations/Niles";
import Buchanan from "./pages/locations/Buchanan";
import BentonHarbor from "./pages/locations/BentonHarbor";
import NewBuffalo from "./pages/locations/NewBuffalo";
import Organizing from "./pages/services/Organizing";
import SameDayOrganizing from "./pages/services/SameDayOrganizing";
import SameDayCleaning from "./pages/services/SameDayCleaning";
import Cleaning from "./pages/Cleaning";
import Store from "./pages/Store";
import StructuredData from "./components/StructuredData";
import { useSecurityMonitoring } from "@/hooks/useSecurityMonitoring";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  useSecurityMonitoring();
  
  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GoogleAnalytics />
        <StructuredData />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/book" element={<Book />} />
          <Route path="/admin" element={<Admin />} />
          
          {/* Location pages - Southwest Michigan */}
          <Route path="/st-joseph-michigan-organizing-cleaning" element={<StJoseph />} />
          <Route path="/niles-michigan-organizing-cleaning" element={<Niles />} />
          <Route path="/buchanan-michigan-organizing-cleaning" element={<Buchanan />} />
          <Route path="/benton-harbor-michigan-organizing-cleaning" element={<BentonHarbor />} />
          <Route path="/new-buffalo-michigan-organizing-cleaning" element={<NewBuffalo />} />
          <Route path="/chicago" element={<Chicago />} />
          
          {/* Service pages */}
          <Route path="/organizing" element={<Organizing />} />
          <Route path="/same-day-organizing" element={<SameDayOrganizing />} />
          <Route path="/same-day-cleaning" element={<SameDayCleaning />} />
          <Route path="/cleaning" element={<Cleaning />} />
          
          {/* Legacy service page redirects */}
          <Route path="/home-reset" element={<Organizing />} />
          <Route path="/same-day" element={<SameDayOrganizing />} />
          <Route path="/adhd-systems" element={<Organizing />} />
          <Route path="/real-estate-prep" element={<Organizing />} />
          
          {/* Content pages */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/store" element={<Store />} />
          
          {/* Legal pages */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          
          {/* Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <PerformanceOptimizer>
        <AppContent />
      </PerformanceOptimizer>  
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
